import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Video } from "lucide-react";

const tzName = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const isod = (d) => d.toISOString().slice(0, 10);

function DayNav({ date, setDate, prefix }) {
  return (
    <div className="flex items-center justify-between max-w-xl my-3">
      <Button variant="outline" size="sm" onClick={() => setDate(new Date(date.getTime() - 864e5))} data-testid={`${prefix}-prev`}>Prev</Button>
      <span className="text-sm font-medium text-slate-700" data-testid={`${prefix}-date`}>{date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
      <Button variant="outline" size="sm" onClick={() => setDate(new Date(date.getTime() + 864e5))} data-testid={`${prefix}-next`}>Next</Button>
    </div>
  );
}

function Slots({ slots, busy, onPick, prefix, emptyId }) {
  if (slots === null) return <p className="text-sm text-slate-400 py-6">Loading times…</p>;
  if (slots.length === 0) return <p className="text-sm text-slate-400 py-6" data-testid={emptyId}>No available times this day. Try another date.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-w-xl">
      {slots.map((s) => <button key={s.start} disabled={busy} onClick={() => onPick(s)} data-testid={`${prefix}-${s.start}`}
        className="rounded-lg border border-slate-200 hover:border-[#006090] hover:bg-[#006090]/5 py-2 text-sm font-medium text-slate-700 disabled:opacity-50">{s.label}</button>)}
    </div>
  );
}

export function PortalScheduling() {
  const [hosts, setHosts] = useState(null);
  const [sel, setSel] = useState(null);
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { api.get("/portal/scheduling/hosts").then((r) => setHosts(r.data.hosts || [])).catch(() => setHosts([])); }, []);
  useEffect(() => {
    if (!sel) return;
    setSlots(null);
    api.get(`/portal/scheduling/hosts/${sel.host.host_user_id}/slots`, { params: { appointment_type_id: sel.type.id, date: isod(date), tz: tzName() } })
      .then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
  }, [sel, date]);
  const book = async (slot) => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/scheduling/book", { host_user_id: sel.host.host_user_id, appointment_type_id: sel.type.id, start: slot.start });
      setDone(data); toast.success("Appointment booked");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (done) return (
    <div data-testid="portal-scheduling-done" className="max-w-lg">
      <h2 className="font-display text-xl font-bold text-slate-900">Appointment Confirmed</h2>
      <p className="text-slate-600 mt-1">Your {done.appointment_type} with {done.host} is confirmed.</p>
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm space-y-1">
        <p><span className="text-slate-400">When:</span> {new Date(done.start).toLocaleString()} · {done.timezone}</p>
        {done.meeting_link ? <a href={done.meeting_link} target="_blank" rel="noreferrer" className="text-[#006090] underline">Microsoft Teams link</a> : done.location ? <p><span className="text-slate-400">Location:</span> {done.location}</p> : null}
      </div>
      <Button className="mt-4" variant="outline" onClick={() => { setDone(null); setSel(null); }} data-testid="portal-scheduling-again">Schedule another</Button>
    </div>
  );

  return (
    <div data-testid="portal-scheduling">
      <p className="text-sm text-slate-600 mb-5">Schedule time with the people supporting your journey.</p>
      {hosts === null ? <p className="text-sm text-slate-400">Loading…</p>
        : hosts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="portal-scheduling-empty">
            <p className="text-slate-600">We don't currently have an online appointment available for you. Please contact your recruiting team or check back soon.</p>
          </div>
        ) : !sel ? (
          <div className="space-y-4">{hosts.map((h) => (
            <div key={h.host_user_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`portal-host-${h.host_user_id}`}>
              <p className="font-semibold text-slate-900">{h.name}</p>
              <p className="text-xs text-slate-500">{h.title}</p>
              <div className="mt-3 flex flex-wrap gap-2">{h.meeting_types.map((t) => (
                <button key={t.id} onClick={() => { setSel({ host: h, type: t }); setDate(new Date()); }} data-testid={`portal-type-${t.id}`}
                  className="rounded-lg border border-slate-200 hover:border-[#006090] hover:bg-[#006090]/5 px-3 py-2 text-left">
                  <span className="text-sm font-medium text-slate-800">{t.name}</span>
                  <span className="block text-xs text-slate-400">{t.duration_min} min</span>
                </button>))}</div>
            </div>))}</div>
        ) : (
          <div className="max-w-xl" data-testid="portal-scheduling-slots">
            <button onClick={() => setSel(null)} className="text-sm text-slate-500 hover:text-slate-700 mb-3" data-testid="portal-scheduling-back">← Back</button>
            <p className="font-semibold text-slate-900">{sel.type.name} with {sel.host.name}</p>
            <DayNav date={date} setDate={setDate} prefix="portal-sched" />
            <p className="text-xs text-slate-400 mb-2">Times in {tzName()}</p>
            <Slots slots={slots} busy={busy} onPick={book} prefix="portal-slot" emptyId="portal-no-slots" />
          </div>
        )}
    </div>
  );
}

function MeetingCard({ m, busy, onReschedule, onCancel }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`portal-meeting-${m.id}`}>
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-900">{m.appointment_type}</p>
        <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === "cancelled" ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-700"}`}>{m.status}</span>
      </div>
      <p className="text-sm text-slate-600 mt-1">{m.start ? new Date(m.start).toLocaleString() : ""} {m.timezone ? `· ${m.timezone}` : ""}</p>
      <p className="text-sm text-slate-600">Host: {m.host_name || "—"} · {(m.mode || "").replace("_", " ")}</p>
      {m.status !== "cancelled" && onCancel && (
        <div className="mt-3 flex flex-wrap gap-2">
          {m.meeting_link && <a href={m.meeting_link} target="_blank" rel="noreferrer"><Button size="sm" className="bg-[#006090] hover:bg-[#004E75]"><Video className="w-3.5 h-3.5 mr-1" />Join Teams</Button></a>}
          <Button size="sm" variant="outline" onClick={() => onReschedule(m)} data-testid={`portal-meeting-reschedule-${m.id}`}>Reschedule</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onCancel(m)} data-testid={`portal-meeting-cancel-${m.id}`} className="text-rose-600 border-rose-200 hover:bg-rose-50">Cancel</Button>
        </div>
      )}
    </div>
  );
}

export function PortalAppointments({ onBook }) {
  const [data, setData] = useState(null);
  const [rx, setRx] = useState(null);
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/portal/meetings").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], past: [] }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!rx) return;
    setSlots(null);
    api.get(`/portal/meetings/${rx.id}/reschedule-slots`, { params: { date: isod(date), tz: tzName() } })
      .then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
  }, [rx, date]);
  const cancel = async (m) => {
    if (!window.confirm("Cancel this appointment?")) return;
    setBusy(true);
    try { await api.post(`/portal/meetings/${m.id}/cancel`); toast.success("Appointment cancelled"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doReschedule = async (slot) => {
    setBusy(true);
    try { await api.post(`/portal/meetings/${rx.id}/reschedule`, { start: slot.start }); toast.success("Appointment rescheduled"); setRx(null); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (rx) return (
    <div data-testid="portal-reschedule">
      <button onClick={() => setRx(null)} className="text-sm text-slate-500 hover:text-slate-700 mb-3">← Back to appointments</button>
      <h2 className="font-display text-xl font-bold text-slate-900">Reschedule {rx.appointment_type}</h2>
      <p className="text-sm text-slate-600 mt-1">with {rx.host_name || "your host"} · pick a new time below.</p>
      <DayNav date={date} setDate={setDate} prefix="portal-rx" />
      <Slots slots={slots} busy={busy} onPick={doReschedule} prefix="portal-rx-slot" emptyId="portal-rx-no-slots" />
    </div>
  );

  return (
    <div data-testid="portal-appointments">
      {data === null ? <p className="text-sm text-slate-400">Loading…</p> : (
        <>
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Upcoming</h2>
          {(data.upcoming || []).length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 mb-6 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-slate-500">No upcoming appointments.</p>
              <Button size="sm" variant="outline" onClick={onBook} data-testid="portal-appointments-book">Book a Meeting</Button>
            </div>
          ) : <div className="space-y-3 mb-8">{data.upcoming.map((m) => <MeetingCard key={m.id} m={m} busy={busy} onCancel={cancel} onReschedule={(x) => { setRx(x); setDate(new Date()); }} />)}</div>}
          {(data.past || []).length > 0 && <>
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Past</h2>
            <div className="space-y-3 opacity-80">{data.past.map((m) => <MeetingCard key={m.id} m={m} />)}</div>
          </>}
        </>
      )}
    </div>
  );
}
